import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate, formatNumber } from '../../utils/formatters.js';

const STATUS_VARIANT = { OPEN: 'warning', FULFILLED: 'success', CANCELLED: 'neutral' };

export function BackordersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('OPEN');
  const [error, setError] = useState(null);

  const list = useQuery({ queryKey: ['inventory', 'backorders', status], queryFn: () => inventoryApi.listBackorders(status ? { status } : {}).then((r) => r.items) });
  const rows = list.data || [];

  const fulfill = useMutation({
    mutationFn: (id) => inventoryApi.fulfillBackorder(id),
    onSuccess: () => { setError(null); queryClient.invalidateQueries({ queryKey: ['inventory', 'backorders'] }); },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });

  const columns = [
    { key: 'product', label: t('bo.col.product'), render: (r) => <span><strong>{r.productSku}</strong> <span className="muted text-sm">{r.productName}</span></span> },
    { key: 'order', label: t('bo.col.order'), render: (r) => <span className="mono">{r.orderNumber || r.orderId?.slice(0, 8)}</span> },
    { key: 'qty', label: t('bo.col.quantity'), align: 'right', render: (r) => <span className="mono num"><strong>{formatNumber(r.quantity)}</strong></span> },
    { key: 'avail', label: t('bo.col.available'), align: 'right', render: (r) => <span className="mono num" style={{ color: Number(r.available) >= Number(r.quantity) ? 'var(--color-success, #059669)' : 'var(--color-danger, #dc2626)' }}>{r.available != null ? formatNumber(r.available) : '-'}</span> },
    { key: 'date', label: t('bo.col.date'), render: (r) => formatDate(r.createdAt) },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{t(`bo.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => (r.status === 'OPEN' ? (
      <Button size="sm" variant="primary" disabled={fulfill.isPending || Number(r.available) < Number(r.quantity)} onClick={() => fulfill.mutate(r.id)}>{t('bo.action.fulfill')}</Button>
    ) : null) },
  ];

  const openCount = rows.filter((r) => r.status === 'OPEN').length;
  const ready = rows.filter((r) => r.status === 'OPEN' && Number(r.available) >= Number(r.quantity)).length;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('bo.title')}</h1>
          <p className="muted">{t('bo.subtitle')}</p>
        </div>
        <div className="page-actions">
          <select className="input" style={{ maxWidth: 170 }} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="OPEN">{t('bo.status.OPEN')}</option>
            <option value="FULFILLED">{t('bo.status.FULFILLED')}</option>
            <option value="">{t('bo.all')}</option>
          </select>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('bo.metric.open')} value={String(status === 'OPEN' ? rows.length : openCount)} trendVariant={openCount > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('bo.metric.ready')} value={String(ready)} trendVariant={ready > 0 ? 'success' : 'neutral'} />
      </div>

      {error && <div className="alert danger" style={{ marginBottom: 'var(--space-3)' }}>{error}</div>}
      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('bo.empty')} />
    </section>
  );
}
